import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Snackbar,
  TextField,
} from "@mui/material";

import { useDisclosure } from "hooks";

import { ShipmentTemplateListElement } from "./components/ShipmentTemplateListElement";

import type { ShipmentTemplate } from "types/shipment";

interface Props {
  error: string | null;
  loading: boolean;
  templates: ShipmentTemplate[];
  onTemplateClicked: (template: ShipmentTemplate) => void;
  selectedTemplateId: number | null;
  onDeleteTemplate: (template: ShipmentTemplate) => void;
  deletedTemplate: boolean;
  onCloseDeletedTemplate: VoidFunction;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export function ShipmentTemplateMenu(props: Props) {
  const {
    error,
    loading,
    templates,
    onTemplateClicked,
    selectedTemplateId,
    onDeleteTemplate,
    deletedTemplate,
    onCloseDeletedTemplate,
    searchQuery,
    onSearchQueryChange,
  } = props;

  const {
    isOpen: appliedOpen,
    onOpen: onOpenApplied,
    onClose: onCloseApplied,
  } = useDisclosure(false);

  return (
    <Box>
      <Autocomplete
        options={templates}
        filterOptions={(x) => x}
        getOptionLabel={(t) => t.name}
        value={null}
        // A template is applied once and never recalculated, so it must not be
        // selectable until everything it is resolved against has arrived.
        disabled={loading}
        inputValue={searchQuery}
        onInputChange={(_, value, reason) => {
          if (reason === "input" || reason === "clear")
            onSearchQueryChange(value);
        }}
        onChange={(_, template) => {
          if (template) {
            onTemplateClicked(template);
            onSearchQueryChange("");
            onOpenApplied();
          }
        }}
        loading={loading}
        loadingText="Loading templates..."
        noOptionsText={error ?? "No templates found."}
        openOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select a template..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <SearchIcon sx={{ color: "text.secondary", mr: 0.5 }} />
              ),
              endAdornment: loading ? (
                <CircularProgress size={20} />
              ) : (
                params.InputProps.endAdornment
              ),
            }}
          />
        )}
        renderOption={(optionProps, template) => {
          const { key, ...liProps } = optionProps;
          return (
            <ShipmentTemplateListElement
              key={key ?? template.id}
              liProps={liProps}
              template={template}
              selectedTemplateId={selectedTemplateId}
              onDeleteTemplate={onDeleteTemplate}
            />
          );
        }}
      />
      <Snackbar
        open={appliedOpen}
        autoHideDuration={2000}
        onClose={onCloseApplied}
      >
        <Alert
          onClose={onCloseApplied}
          severity="success"
          sx={{ width: "100%" }}
        >
          Template Applied!
        </Alert>
      </Snackbar>
      <Snackbar
        open={deletedTemplate}
        autoHideDuration={2000}
        onClose={onCloseDeletedTemplate}
      >
        <Alert
          onClose={onCloseDeletedTemplate}
          severity="success"
          sx={{ width: "100%" }}
        >
          Template Deleted
        </Alert>
      </Snackbar>
    </Box>
  );
}
